<template>
  <div :class="{
    'pod-information-section': !standalone,
    'pod-information-section-standalone': standalone,
  }">
    <div class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.fileName') }}</div>
      <div>{{ pod.name }}</div>
    </div>

    <div class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.fileType') }}</div>
      <div>{{ rawAsset.filetype }}</div>
    </div>

    <div class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.fileSize') }}</div>
      <div>{{ formatSize(rawAsset.size || 0, 2) }}</div>
    </div>

    <div class="d-flex pod-information-section-row">
      <div>{{ $t('routes.node.propertiesPodDialogAccess') }}</div>
      <div>{{ pod.teamonly ? $t('common.teamonly') : $t('common.public') }}</div>
    </div>

    <br/>

    <div v-if="isVideoPod" class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.runtime') }}</div>
      <div>{{ videoDuration(parseFloat(rawAsset.duration || 0)) }}</div>
    </div>

    <br/>

    <div class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.resolution') }}</div>
      <div>{{ rawAsset.resolution }}</div>
    </div>

    <div class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.aspectRatio') }}</div>
      <div>{{ rawAsset.aspect }}</div>
    </div>

    <div v-if="isVideoPod" class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.videoCodec') }}</div>
      <div>{{ rawAsset.codec }}</div>
    </div>

    <div v-if="isVideoPod" class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.bitrate') }}</div>
      <div>{{ rawAsset.bitrate }}</div>
    </div>

    <div v-if="isVideoPod" class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.fps') }}</div>
      <div>{{ rawAsset.framerate ? parseFloat(rawAsset.framerate).toFixed(2) : '' }}</div>
    </div>

    <br/>

    <div v-if="isVideoPod" class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.audioCodec') }}</div>
      <div>{{ rawAsset.audiocodec }}</div>
    </div>

    <div v-if="isVideoPod" class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.audioChannels') }}</div>
      <div>{{ rawAsset.audiochannels }}</div>
    </div>

    <div v-if="isVideoPod" class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.audioBitrate') }}</div>
      <div>{{ rawAsset.audiobitrate }}</div>
    </div>

    <br/>

    <div v-if="getUploaderName" class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.createdBy') }}</div>
      <div>{{ getUploaderName }}</div>
    </div>

    <div class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.creationDate') }}</div>
      <div>{{ isoToDatetime(pod.createdat) }}</div>
    </div>

    <div v-if="pod.updatedby && pod.updatedby.firstname" class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.lastUpdatedBy') }}</div>
      <div>{{ pod.updatedby.firstname }} {{ pod.updatedby.lastname }}</div>
    </div>

    <div class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.lastUpdatedDate') }}</div>
      <div>{{ isoToDatetime(pod.updatedat) }}</div>
    </div>

    <div class="d-flex pod-information-section-row" v-if="lastModifiedOriginal">
      <div>{{ $t('routes.node.originalLastModified') }}</div>
      <div>{{ toDateTime(lastModifiedOriginal) }}</div>
    </div>

    <div class="d-flex pod-information-section-row">
      <div>{{ $t('routes.pod.menuInfo.serviceUsed') }}</div>
      <div></div>
    </div>

  </div>

  <br/>

  <div v-if="!disableNotes" class="d-flex" style="height: 36px;">
    <div class="flex-grow-1 d-flex align-center">
      <h3 id="media-info-note-label">{{ $t('routes.pod.menuInfo.note') }}</h3>
    </div>
    <div v-show="isNoteChanged" class="flex-grow-0 text-right">
      <v-tooltip location="bottom" :aria-label="$t('common.cancel')">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            variant="text"
            width="36"
            height="36"
            rounded="10"
            @click.prevent="noteCancel()"
            :aria-label="$t('common.cancel')"
            tile icon
          >
            <v-icon size="small" color="primary">mdi-cancel</v-icon>
          </v-btn>
        </template>

        {{ $t('common.cancel') }}
      </v-tooltip>

      <v-tooltip location="bottom" :aria-label="$t('common.save')">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            variant="text"
            width="36"
            height="36"
            rounded="10"
            @click.prevent="noteSave()"
            :aria-label="$t('common.save')"
            tile icon
          >
            <v-icon size="small" color="primary">mdi-floppy</v-icon>
          </v-btn>
        </template>

        {{ $t('common.save') }}
      </v-tooltip>
    </div>
  </div>

  <div v-if="!disableNotes" class="pb-2">
    <div>
      <v-textarea
        class="media-list-notes-textarea"
        v-model="noteModel"
        :disabled="!$canControlNodeAndPod()"
        :error="noteError"
        v-on:focus="setNeutral"
        variant="solo-filled"
        flat
        density="compact"
        rows="6"
        hide-details="true"
        aria-labelledby="media-info-note-label"
        :aria-label="$t('routes.pod.menuInfo.note')"
      />
    </div>
  </div>
</template>

<script>
import { isoToDatetime, toDateTime } from '@/components/formatters/timestamp.js';
import { videoDuration } from '@/components/formatters/duration.js';
import { formatSize } from '@/components/formatters.js';
import { usePodStore } from '@/stores/PodStore.js';

export default {
  name: 'MediaInformationNote',
  props: {
    pod: Object,
    disableNotes: {
      type: Boolean,
      required: false,
      default: false,
    },
    standalone: {
      type: Boolean,
      default: false,
    },
  },
  data: function() {
    return {
      noteModel: '',
      noteSuccess: false,
      noteError: false,
      mediaInformation: 'file',
    };
  },
  mounted: function() {
    this.noteModel = this.pod ? this.pod.note : "";
  },
  methods: {
    toDateTime,
    isoToDatetime,
    videoDuration,
    formatSize,
    noteCancel: function() {
      this.noteModel = this.pod.note;
    },
    noteSave: async function() {
      const copiedPod = JSON.parse(JSON.stringify(this.pod));   // deep copy
      copiedPod.note = this.noteModel;

      const podStore = usePodStore();

      try {
        await podStore.updatePod(copiedPod);
        this.setSuccess(this.$t('routes.pod.menuInfo.noteUpdated\''));
        this.noteModel = this.pod.note;
      } catch (error) {
        console.error(error);
        this.setError();
      }
    },
    setError: function() {
      this.noteSuccess = false;
      this.noteError = true;
    },
    setSuccess: function() {
      this.noteSuccess = true;
      this.noteError = false;
    },
    setNeutral: function() {
      this.noteSuccess = false;
      this.noteError = false;
    }
  },
  computed: {
    rawAsset: function() {
      if (this.pod && this.pod.asset && this.pod.asset.raw && this.pod.asset.raw[0]) {
        return this.pod.asset.raw[0];
      }
      return {};
    },

    isNoteChanged: function() {
      return this.pod && (this.noteModel !== this.pod.note);
    },
    isVideoPod: function() {
      const podStore = usePodStore();
      return podStore.isVideoPod;
    },
    getUploaderName: function() {
      if (this.pod.jsondata?.uploaderName) {
        return `${this.$t("routes.node.externalUploader")}: ${this.pod.jsondata.uploaderName}`;
      }

      if (this.pod) {
        if (this.pod.createdby) {
          return `${this.pod.createdby.firstname} ${this.pod.createdby.lastname}`;
        }

        return this.$t('common.deletedUser');
      }

      return null;
    },
    lastModifiedOriginal: function() {
      if (this?.pod?.jsondata?.lastModified) {
        return this?.pod?.jsondata?.lastModified;
      }

      return null;
    }
  }
};
</script>
